import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { AppContext } from '../../context/AppContext';
import { Socio, Carrera, Sancion, Apelacion } from '../../models/types';
import { FaUser, FaTrophy, FaFlag, FaExclamationTriangle, FaHistory, FaEdit, FaUpload, FaIdCard } from 'react-icons/fa';
import { SocioServiceFirestore } from '../../services/SocioServiceFirestore';

import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, CartesianGrid } from 'recharts';
// Estilos
import { theme, commonStyles } from '../../theme';

const ProfileContainer = styled.div`
  padding: 32px;
  max-width: 1280px;
  margin: 0 auto;
  background: ${theme.colors.background.default};
  border-radius: ${theme.borderRadius.large};
  box-shadow: ${theme.shadows.large};
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 36px;
  gap: 36px;
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const ProfilePhoto = styled.div`
  width: 200px;
  height: 200px;
  border-radius: 50%;
  overflow: hidden;
  background-color: ${theme.colors.background.paper};
  box-shadow: ${theme.shadows.medium};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s cubic-bezier(0.4,0,0.2,1);
    border-radius: 50%;
  }
  &:hover img {
    transform: scale(1.08) rotate(-2deg);
  }
`;

const ProfileInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ProfileName = styled.h1`
  font-size: 2.2rem;
  margin: 0 0 10px 0;
  color: ${theme.colors.primary.main};
  font-weight: ${theme.typography.fontWeights.bold};
  letter-spacing: 1px;
`;

const ProfileTeam = styled.h2`
  font-size: 1.3rem;
  margin: 0 0 20px 0;
  color: ${theme.colors.secondary.main};
  font-weight: ${theme.typography.fontWeights.semibold};
`;

const ProfileBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 24px;
`;

const Badge = styled.div`
  background: ${() => `linear-gradient(90deg, ${theme.colors.primary.main} 60%, ${theme.colors.secondary.main} 100%)`};
  color: ${theme.colors.primary.contrastText};
  padding: 7px 16px;
  border-radius: 999px;
  font-size: 1rem;
  font-weight: ${theme.typography.fontWeights.semibold};
  box-shadow: ${theme.shadows.small};
  display: flex;
  align-items: center;
  letter-spacing: 0.5px;
  transition: box-shadow 0.2s;
  svg {
    margin-right: 8px;
  }
  &:hover {
    box-shadow: 0 0 14px 2px ${theme.colors.secondary.light};
  }
`;

const TabsContainer = styled.div`
  margin-bottom: 20px;
`;

const TabsHeader = styled.div`
  display: flex;
  border-bottom: 1px solid #ddd;
  overflow-x: auto;
  
  &::-webkit-scrollbar {
    height: 5px;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: #ccc;
    border-radius: 5px;
  }
`;

const Tab = styled.div<{ $active: boolean }>`
  padding: 15px 20px;
  cursor: pointer;
  font-weight: ${props => props.$active ? 'bold' : 'normal'};
  color: ${props => props.$active ? theme.colors.primary.main : '#666'};
  border-bottom: ${props => props.$active ? `3px solid ${theme.colors.primary.main}` : 'none'};
  white-space: nowrap;
  
  &:hover {
    background-color: #f9f9f9;
  }
`;

const TabContent = styled.div`
  padding: 20px 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
`;

const StatCard = styled.div`
  ${commonStyles.card}
  padding: 28px 20px;
  text-align: center;
  position: relative;
  overflow: hidden;
  transition: box-shadow 0.2s;
  &:hover {
    box-shadow: 0 0 16px 2px ${theme.colors.primary.light};
    transform: translateY(-2px) scale(1.03);
  }
  &:after {
    content: '';
    position: absolute;
    left: 0; right: 0; bottom: 0;
    height: 4px;
    background: linear-gradient(90deg, ${theme.colors.secondary.main} 60%, ${theme.colors.primary.main} 100%);
    border-radius: 0 0 ${theme.borderRadius.medium} ${theme.borderRadius.medium};
    opacity: 0.7;
  }
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: ${theme.colors.primary.main};
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: #666;
`;

const LicensePointsContainer = styled.div`
  margin-bottom: 30px;
`;

const LicensePointsHeader = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 15px;
  color: ${theme.colors.primary.main};
`;

const LicensePointsBar = styled.div`
  background-color: #f0f0f0;
  height: 30px;
  border-radius: 15px;
  overflow: hidden;
  margin-bottom: 10px;
`;

const LicensePointsFill = styled.div<{ $points: number }>`
  height: 100%;
  width: ${props => (props.$points / 12) * 100}%;
  background: ${props => {
    if (props.$points >= 9) return `linear-gradient(90deg, ${theme.colors.primary.main} 60%, ${theme.colors.secondary.main} 100%)`;
    if (props.$points >= 6) return theme.colors.warning.main;
    return theme.colors.error.main;
  }};
  box-shadow: 0 0 12px 1px ${theme.colors.secondary.light};
  transition: width 0.5s cubic-bezier(0.4,0,0.2,1), background 0.3s;
`;

const LicensePointsText = styled.div`
  font-size: 1rem;
  color: #666;
`;

const Button = styled.button`
  background: linear-gradient(90deg, ${theme.colors.primary.main} 60%, ${theme.colors.secondary.main} 100%);
  color: ${theme.colors.primary.contrastText};
  border: none;
  padding: 12px 24px;
  border-radius: ${theme.borderRadius.medium};
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 1rem;
  font-weight: ${theme.typography.fontWeights.bold};
  box-shadow: ${theme.shadows.small};
  transition: background 0.2s, box-shadow 0.2s;
  svg {
    margin-right: 10px;
  }
  &:hover {
    background: linear-gradient(90deg, ${theme.colors.secondary.main} 60%, ${theme.colors.primary.main} 100%);
    box-shadow: 0 0 16px 2px ${theme.colors.secondary.light};
  }
  &:disabled {
    background: ${theme.colors.background.paper};
    color: ${theme.colors.text.disabled};
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th, td {
    padding: 12px 15px;
    text-align: left;
    border-bottom: 1px solid #ddd;
  }
  
  th {
    background-color: #f8f9fa;
    font-weight: bold;
    color: ${theme.colors.primary.main};
  }
  
  tr:hover {
    background-color: #f5f5f5;
  }
`;

const SocioProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, loadData } = useContext(AppContext);
  const [socio, setSocio] = useState<Socio | null>(null);
  const [activeTab, setActiveTab] = useState('estadisticas');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Cargar datos del socio
  useEffect(() => {
    const loadSocio = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const socioData = data.socios.find((s: Socio) => s.id === id);
        
        if (socioData) {
          // Inicializar valores por defecto si no existen
          const socioCompleto: Socio = {
            ...socioData,
            puntosLicencia: socioData.puntosLicencia ?? 12,
            nivelReputacion: socioData.nivelReputacion ?? 80,
            carrerasLimpias: socioData.carrerasLimpias ?? 0,
            incidentesPorCarrera: socioData.incidentesPorCarrera ?? 0,
            insignias: socioData.insignias ?? [],
            estadisticas: socioData.estadisticas ?? {
              carrerasCompletadas: 0,
              victorias: 0,
              podios: 0,
              polePositions: 0,
              vueltasRapidas: 0,
              abandonos: 0,
              sancionesRecibidas: 0,
              posicionMediaClasificacion: 0,
              posicionMediaCarrera: 0,
              puntosPromedioPorCarrera: 0
            }
          };
          
          setSocio(socioCompleto);
        } else {
          setError('No se encontró el socio solicitado');
        }
      } catch (err) {
        console.error('Error al cargar el socio:', err);
        setError('Error al cargar los datos del socio');
      } finally {
        setLoading(false);
      }
    };
    
    loadSocio();
  }, [id, data]);
  
  // Obtener carreras del piloto (solo carreras publicadas/celebradas)
  const getCarrerasPiloto = () => {
    if (!socio) return [];
    
    return data.carreras
      .filter((carrera: Carrera) =>
        // Solo carreras publicadas/celebradas
        carrera.publicada === true &&
        // Y que el piloto haya participado
        carrera.resultados.some((resultado: any) => resultado.socioId === socio.id)
      )
      .sort((a: Carrera, b: Carrera) => a.nombre.localeCompare(b.nombre));
  };
  
  // Obtener sanciones del piloto
  const getSancionesPiloto = () => {
    if (!socio) return [];
    
    return data.sanciones.filter((sancion: Sancion) => sancion.socioId === socio.id);
  };
  
  // Calcular estadísticas del piloto
  const calcularEstadisticas = () => {
    if (!socio) return null;
    
    // Obtener solo carreras publicadas/celebradas
    const carreras = getCarrerasPiloto();
    const sanciones = getSancionesPiloto();
    
    // Inicializar contadores
    let carrerasCompletadas = 0;
    let victorias = 0;
    let podios = 0;
    let polePositions = 0;
    let vueltasRapidas = 0;
    let abandonos = 0;
    let sancionesRecibidas = sanciones.length;
    let posicionMediaCarrera = 0;
    let puntosPromedioPorCarrera = 0;
    let totalPosicionesCarrera = 0;
    let totalPuntos = 0;
    let carrerasConPosicionValida = 0;
    
    // Procesar cada carrera para calcular estadísticas
    carreras.forEach((carrera) => {
      // Buscar el resultado del piloto en esta carrera
      const resultado = carrera.resultados.find(r => r.socioId === socio.id);
      if (resultado) {
        carrerasCompletadas++;
        // Sumar puntos (usar puntos de la carrera)
        totalPuntos += resultado.puntos || 0;
        
        // Contar victorias, podios, poles, etc.
        if (resultado.posicion === 1) victorias++;
        if (resultado.posicion && resultado.posicion <= 3) podios++;
        if (resultado.pole || resultado.polePosition) polePositions++;
        if (resultado.vueltaRapida) vueltasRapidas++;
        if (resultado.dnf) abandonos++;
        
        // Solo contar posiciones válidas para la media (no DNF)
        if (resultado.posicion && !resultado.dnf) {
          totalPosicionesCarrera += resultado.posicion;
          carrerasConPosicionValida++;
        }
      }
    });
    
    // Calcular promedios
    posicionMediaCarrera = carrerasConPosicionValida > 0 ? totalPosicionesCarrera / carrerasConPosicionValida : 0;
    puntosPromedioPorCarrera = carrerasCompletadas > 0 ? totalPuntos / carrerasCompletadas : 0;
    
    // Calcular posición en la clasificación general
    const { posicionGeneral, totalPilotosEnLiga } = obtenerPosicionEnLiga(socio, totalPuntos);
    
    // Calcular la reputación del piloto
    const reputacion = calcularReputacion({
      carrerasCompletadas,
      victorias,
      podios,
      abandonos,
      sancionesRecibidas,
      posicionMediaCarrera,
      posicionGeneral,
      totalPilotosEnLiga
    });
    
    return {
      carrerasCompletadas,
      victorias,
      podios,
      polePositions,
      vueltasRapidas,
      abandonos,
      sancionesRecibidas,
      posicionMediaCarrera,
      puntosPromedioPorCarrera,
      totalPuntos,
      posicionGeneral,
      totalPilotosEnLiga,
      reputacion
    };
  };
  
  // Función para obtener la posición del piloto en la liga
  const obtenerPosicionEnLiga = (piloto: Socio | null, puntosCalculados: number = 0) => {
    if (!piloto) return { posicionGeneral: 0, totalPilotosEnLiga: 0 };
    
    // Determinar la liga o categoría del piloto
    const ligaId = piloto.ligaAsignada;
    const categoria = piloto.Categoria;
    
    // Buscar la liga por ID o categoría
    const ligaDelPiloto = data.ligas.find(liga => {
      return liga.id === ligaId || (categoria && liga.nombre.includes(categoria));
    });
    
    // Obtener todos los pilotos en la misma liga/categoría (sin filtrar por participación)
    const pilotosEnLiga = data.socios.filter(s => {
      // Verificar si está en la misma liga
      const enMismaLiga = ligaDelPiloto && s.ligaAsignada === ligaDelPiloto.id;
      // Verificar si está en la misma categoría
      const enMismaCategoria = categoria && s.Categoria === categoria;
      
      // Incluir a todos los pilotos de la liga, no solo los que han participado
      return (enMismaLiga || enMismaCategoria);
    });
    
    // Si no hay pilotos en la liga, devolver posición 1 de 1
    if (pilotosEnLiga.length === 0) {
      return { posicionGeneral: 1, totalPilotosEnLiga: 1 };
    }
    
    // Calcular los puntos totales de cada piloto
    const pilotosConPuntos = pilotosEnLiga.map((p: Socio) => {
      // Si es el piloto actual, usar los puntos calculados
      if (p.id === piloto.id) {
        return { ...p, puntosTotalesCalculados: puntosCalculados };
      }
      
      // Para otros pilotos, calcular sus puntos totales
      let puntosTotales = 0;
      data.carreras.forEach(carrera => {
        if (carrera.publicada) {
          const resultado = carrera.resultados.find(r => r.socioId === p.id);
          if (resultado) {
            puntosTotales += resultado.puntos || 0;
          }
        }
      });
      
      return { ...p, puntosTotalesCalculados: puntosTotales };
    });
    
    // Ordenar por puntos totales (de mayor a menor)
    const pilotosOrdenados = [...pilotosConPuntos].sort((a: Socio & { puntosTotalesCalculados?: number }, b: Socio & { puntosTotalesCalculados?: number }) => {
      return (b.puntosTotalesCalculados || 0) - (a.puntosTotalesCalculados || 0);
    });
    
    // Encontrar la posición del piloto actual
    const posicion = pilotosOrdenados.findIndex((p: Socio & { puntosTotalesCalculados?: number }) => p.id === piloto.id) + 1;
    
    return {
      posicionGeneral: posicion > 0 ? posicion : pilotosOrdenados.length,
      totalPilotosEnLiga: pilotosOrdenados.length
    };
  };
  
  // Calcular la reputación del piloto basado en múltiples factores
  const calcularReputacion = (stats: {
    carrerasCompletadas: number;
    victorias: number;
    podios: number;
    abandonos: number;
    sancionesRecibidas: number;
    posicionMediaCarrera: number;
    posicionGeneral: number;
    totalPilotosEnLiga: number;
  }) => {
    // Si no hay carreras completadas, asignar una reputación base de 50
    if (stats.carrerasCompletadas === 0) return 50;
    
    // 1. Factor de posición en la clasificación general (40% del total)
    // Mejor posición = mayor puntuación
    let factorPosicion = 40;
    if (stats.totalPilotosEnLiga > 1 && stats.posicionGeneral > 1) {
      // Calcular qué porcentaje representa su posición en la clasificación
      const porcentajePosicion = (stats.posicionGeneral - 1) / (stats.totalPilotosEnLiga - 1);
      factorPosicion = Math.round(40 * (1 - porcentajePosicion));
    }
    
    // 2. Factor de resultados en carreras (40% del total)
    // Base de 20 puntos + bonificaciones por victorias y podios - penalizaciones por abandonos
    let factorResultados = 20;
    
    // Calcular ratios
    const ratioVictorias = stats.carrerasCompletadas > 0 ? stats.victorias / stats.carrerasCompletadas : 0;
    const ratioPodiosNoVictorias = stats.carrerasCompletadas > 0 ? 
      Math.max(0, (stats.podios - stats.victorias) / stats.carrerasCompletadas) : 0;
    const ratioAbandonos = stats.carrerasCompletadas > 0 ? stats.abandonos / stats.carrerasCompletadas : 0;
    
    // Bonificación por victorias (hasta 20 puntos adicionales)
    factorResultados += Math.round(ratioVictorias * 20);
    
    // Bonificación por podios que no son victorias (hasta 15 puntos adicionales)
    factorResultados += Math.round(ratioPodiosNoVictorias * 15);
    
    // Penalización por abandonos (hasta -15 puntos)
    factorResultados -= Math.round(ratioAbandonos * 15);
    
    // Asegurar que esté en el rango 0-40
    factorResultados = Math.max(0, Math.min(40, factorResultados));
    
    // 3. Factor de sanciones (20% del total)
    // Si no tiene sanciones, obtiene los 20 puntos completos
    let factorSanciones = 20;
    if (stats.carrerasCompletadas > 0 && stats.sancionesRecibidas > 0) {
      const ratioSanciones = stats.sancionesRecibidas / stats.carrerasCompletadas;
      // Una sanción por carrera (ratio=1) resta 15 puntos
      factorSanciones = Math.max(0, 20 - Math.round(ratioSanciones * 15));
    }
    
    // Sumar todos los factores para obtener la reputación final (0-100)
    const reputacionTotal = Math.round(factorPosicion + factorResultados + factorSanciones);
    
    // Asegurar que la reputación esté en el rango 0-100
    return Math.max(0, Math.min(100, reputacionTotal));
  };

  if (loading) return <div>Cargando perfil del piloto...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!socio) return <div>No se encontró el piloto</div>;
  
  return (
    <ProfileContainer>
      <ProfileHeader>
        <ProfilePhoto>
          {socio.fotoPerfil ? (
            <img src={socio.fotoPerfil} alt={`${socio.nombre} ${socio.apellido}`} />
          ) : (
            <FaUser size={80} color={theme.colors.text.secondary} />
          )}
        </ProfilePhoto>
        
        <ProfileInfo>
          <ProfileName>{socio.display_name || `${socio.nombre || ''} ${socio.apellido || ''}`}</ProfileName>
          <ProfileTeam>{socio.EQUIPO || 'Sin equipo'}</ProfileTeam>
          
          <ProfileBadges>
            <Badge><FaIdCard /> {socio.Categoria || 'Sin categoría'}</Badge>
            {socio.insignias?.map((insignia, index) => (
              <Badge key={index}><FaTrophy /> {insignia}</Badge>
            ))}
          </ProfileBadges>
          
          
        </ProfileInfo>
      </ProfileHeader>
      
      <LicensePointsContainer>
        <LicensePointsHeader>Puntos de Licencia</LicensePointsHeader>
        <LicensePointsBar>
          <LicensePointsFill $points={socio.puntosLicencia || 12} />
        </LicensePointsBar>
        <LicensePointsText>
          {socio.puntosLicencia || 12}/12 puntos
{socio.puntosLicencia && socio.puntosLicencia < 3 ? (
                <span style={{ color: theme.colors.secondary.main, fontWeight: 'bold' }}> Advertencia: En riesgo de suspensión</span>
) : socio.puntosLicencia && socio.puntosLicencia < 6 ? (
                <span style={{ color: theme.colors.warning.main }}> Precaución: Puntos de licencia bajos</span>
) : (
  <span style={{ color: theme.colors.secondary.main, fontWeight: 'bold' }}> Buen estado</span>
)}
        </LicensePointsText>
      </LicensePointsContainer>
      
      <TabsContainer>
        <TabsHeader>
          <Tab 
            $active={activeTab === 'estadisticas'} 
            onClick={() => setActiveTab('estadisticas')}
          >
            Estadísticas
          </Tab>
          <Tab 
            $active={activeTab === 'historial'} 
            onClick={() => setActiveTab('historial')}
          >
            Historial de Carreras
          </Tab>
          <Tab 
            $active={activeTab === 'sanciones'} 
            onClick={() => setActiveTab('sanciones')}
          >
            Sanciones
          </Tab>
          <Tab 
            $active={activeTab === 'apelaciones'} 
            onClick={() => setActiveTab('apelaciones')}
          >
            Apelaciones
          </Tab>
          <Tab 
            $active={activeTab === 'licencia'} 
            onClick={() => setActiveTab('licencia')}
          >
            Puntos de Licencia
          </Tab>
          <Tab 
            $active={activeTab === 'reputacion'} 
            onClick={() => setActiveTab('reputacion')}
          >
            Reputación
          </Tab>
        </TabsHeader>
        
        <TabContent>
           {activeTab === 'estadisticas' && (
            <>
              <h3>Estadísticas del Piloto</h3>
              
              {/* Tarjeta destacada de Precio en el Mercado */}
              <div style={{ 
                gridColumn: 'span 2', 
                background: 'linear-gradient(135deg, #0f3460 0%, #e4312e 100%)',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '20px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                color: 'white',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <div style={{ fontSize: '16px', marginBottom: '10px', fontWeight: 'bold' }}>
                  PRECIO EN EL MERCADO
                </div>
                <div style={{ 
                  fontSize: '32px', 
                  fontWeight: 'bold',
                  marginBottom: '5px'
                }}>
                  {(() => {
                    const reputacion = calcularEstadisticas()?.reputacion || 0;
                    const sueldoBase = 84000;
                    const precioMercado = Math.round(sueldoBase * (reputacion / 100));
                    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(precioMercado);
                  })()}
                </div>
                <div style={{ fontSize: '14px', opacity: 0.9 }}>
                  Basado en sus estadísticas y rendimiento
                </div>
              </div>
              
              <StatsGrid>
                <StatCard>
                  <StatValue>{calcularEstadisticas()?.carrerasCompletadas || 0}</StatValue>
                  <StatLabel>Carreras Completadas</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>{calcularEstadisticas()?.victorias || 0}</StatValue>
                  <StatLabel>Victorias</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>{calcularEstadisticas()?.podios || 0}</StatValue>
                  <StatLabel>Podios</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>{calcularEstadisticas()?.polePositions || 0}</StatValue>
                  <StatLabel>Pole Positions</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>{calcularEstadisticas()?.vueltasRapidas || 0}</StatValue>
                  <StatLabel>Vueltas Rápidas</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>{socio.puntosTotales}</StatValue>
                  <StatLabel>Puntos Totales</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>{calcularEstadisticas()?.posicionMediaCarrera.toFixed(1) || 0}</StatValue>
                  <StatLabel>Posición Media</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>{calcularEstadisticas()?.reputacion || 0}</StatValue>
                  <StatLabel>Reputación</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>
                    {(() => {
                      const stats = calcularEstadisticas();
                      if (!stats || stats.carrerasCompletadas === 0) return '0%';
                      // Calcular consistencia: carreras terminadas sin abandonos
                      const carrerasTerminadas = stats.carrerasCompletadas - (stats.abandonos || 0);
                      const consistencia = Math.round((carrerasTerminadas / stats.carrerasCompletadas) * 100);
                      return `${consistencia}%`;
                    })()}
                  </StatValue>
                  <StatLabel>Consistencia</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>
                    {(() => {
                      if (!socio) return '-';
                      const carreras = getCarrerasPiloto();
                      if (carreras.length === 0) return '-';
                      
                      // Encontrar la mejor posición en carrera
                      let mejorPosicion = 999;
                      carreras.forEach(carrera => {
                        const resultado = carrera.resultados.find(r => r.socioId === socio.id);
                        if (resultado && resultado.posicion && !resultado.dnf && resultado.posicion < mejorPosicion) {
                          mejorPosicion = resultado.posicion;
                        }
                      });
                      
                      return mejorPosicion === 999 ? '-' : `${mejorPosicion}°`;
                    })()}
                  </StatValue>
                  <StatLabel>Mejor Posición</StatLabel>
                </StatCard>
              </StatsGrid>

              {/* ----------- GRÁFICOS DE ESTADÍSTICAS DEL PILOTO ----------- */}
              <div style={{ marginTop: 48, marginBottom: 24 }}>
                <h4 style={{ color: '#0f3460', fontWeight: 700, marginBottom: 16 }}>Gráficos de Rendimiento</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
                  {/* Gráfico de barras: puntos por carrera */}
                  <div style={{ width: '100%', background: '#FFFFFF', borderRadius: 12, boxShadow: theme.shadows.small, padding: 18 }}>
                    <div style={{ fontWeight: 600, color: '#0f3460', marginBottom: 8 }}>Puntos por carrera</div>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={getCarrerasPiloto().map((carrera) => {
                        const resultado = carrera.resultados.find((r: any) => r.socioId === socio.id);
                        return {
                          nombre: carrera.nombre,
                          puntos: resultado?.puntosTotales || 0
                        };
                      })} barSize={38} margin={{ top: 16, right: 16, left: 0, bottom: 16 }}>
                        <XAxis dataKey="nombre" tick={{ fill: '#0f3460', fontWeight: 500 }} axisLine={{ stroke: '#e4312e' }} tickLine={false} />
                        <YAxis tick={{ fill: '#0f3460', fontWeight: 500 }} axisLine={{ stroke: '#e4312e' }} tickLine={false} />
                        <Tooltip contentStyle={{ background: '#FFFFFF', border: '1px solid #e4312e', color: '#0f3460' }} />
                        <Bar dataKey="puntos" radius={[8, 8, 0, 0]} fill="#0f3460">
                          {/* Bordes rojos en las barras para destacar */}
                          {getCarrerasPiloto().map((_, idx) => (
                            <Cell key={`bar-cell-${idx}`} fill="#0f3460" stroke="#e4312e" strokeWidth={2} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  {/* Gráfico de pastel: distribución de resultados */}
                  <div style={{ width: '100%', background: '#FFFFFF', borderRadius: 12, boxShadow: theme.shadows.small, padding: 18 }}>
                    <div style={{ fontWeight: 600, color: '#0f3460', marginBottom: 8 }}>Distribución de resultados</div>
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Victorias', value: calcularEstadisticas()?.victorias || 0 },
                            { name: 'Podios', value: calcularEstadisticas()?.podios || 0 },
                            { name: 'Abandonos', value: calcularEstadisticas()?.abandonos || 0 },
                            { name: 'Sanciones', value: calcularEstadisticas()?.sancionesRecibidas || 0 }
                          ]}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          label={({ name, value }) => `${name}: ${value}`}
                        >
                          {/* Colores corporativos: azul oscuro, rojo, blanco, gris claro para sanciones */}
                          <Cell fill="#0f3460" />
                          <Cell fill="#e4312e" />
                          <Cell fill="#FFFFFF" stroke="#0f3460" strokeWidth={2} />
                          <Cell fill="#b0b0c0" />
                        </Pie>
                        <Tooltip contentStyle={{ background: '#FFFFFF', border: '1px solid #e4312e', color: '#0f3460' }} />
                        <Legend iconType="circle" layout="horizontal" align="center" verticalAlign="bottom" wrapperStyle={{ color: '#0f3460', fontWeight: 600, fontSize: 16, marginTop: 12 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  {/* Gráfico de línea: puntos acumulados */}
                  <div style={{ width: '100%', background: '#FFFFFF', borderRadius: 12, boxShadow: theme.shadows.small, padding: 18 }}>
                    <div style={{ fontWeight: 600, color: '#0f3460', marginBottom: 8 }}>Puntos acumulados</div>
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart
                        data={(() => {
                          let total = 0;
                          return getCarrerasPiloto().map((carrera) => {
                            const resultado = carrera.resultados.find((r: any) => r.socioId === socio.id);
                            total += resultado?.puntosTotales || 0;
                            return {
                              nombre: carrera.nombre,
                              puntosAcumulados: total
                            };
                          });
                        })()}
                        margin={{ top: 16, right: 16, left: 0, bottom: 16 }}
                      >
                        <XAxis dataKey="nombre" tick={{ fill: '#0f3460', fontWeight: 500 }} axisLine={{ stroke: '#e4312e' }} tickLine={false} />
                        <YAxis tick={{ fill: '#0f3460', fontWeight: 500 }} axisLine={{ stroke: '#e4312e' }} tickLine={false} />
                        <Tooltip contentStyle={{ background: '#FFFFFF', border: '1px solid #e4312e', color: '#0f3460' }} />
                        <Line type="monotone" dataKey="puntosAcumulados" stroke="#e4312e" strokeWidth={4} dot={{ r: 5, fill: '#0f3460', stroke: '#FFFFFF', strokeWidth: 2 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
              {/* Gráfico de radar: resumen de logros */}
<div style={{ width: '100%', background: '#FFFFFF', borderRadius: 12, boxShadow: theme.shadows.small, padding: 18 }}>
  <div style={{ fontWeight: 600, color: '#0f3460', marginBottom: 8 }}>Resumen de logros (Radar)</div>
  <ResponsiveContainer width="100%" height={300}>
    <RadarChart cx="50%" cy="50%" outerRadius={100} data={[
      { stat: 'Victorias', valor: calcularEstadisticas()?.victorias || 0 },
      { stat: 'Podios', valor: calcularEstadisticas()?.podios || 0 },
      { stat: 'Abandonos', valor: calcularEstadisticas()?.abandonos || 0 },
      { stat: 'Sanciones', valor: calcularEstadisticas()?.sancionesRecibidas || 0 },
      { stat: 'Poles', valor: calcularEstadisticas()?.polePositions || 0 },
      { stat: 'V. Rápidas', valor: calcularEstadisticas()?.vueltasRapidas || 0 },
    ]}>
      <PolarGrid stroke="#b0b0c0" />
      <PolarAngleAxis dataKey="stat" tick={{ fill: '#0f3460', fontWeight: 600 }} />
      <PolarRadiusAxis angle={30} domain={[0, 'auto']} tick={{ fill: '#0f3460' }} />
      <Radar name="Estadísticas" dataKey="valor" stroke="#e4312e" fill="#e4312e" fillOpacity={0.4} />
      <Legend wrapperStyle={{ color: '#0f3460', fontWeight: 600, fontSize: 15 }} />
      <Tooltip contentStyle={{ background: '#FFFFFF', border: '1px solid #e4312e', color: '#0f3460' }} />
    </RadarChart>
  </ResponsiveContainer>
</div>
{/* Gráfico de barras horizontales: tipos de resultados */}
<div style={{ width: '100%', background: '#FFFFFF', borderRadius: 12, boxShadow: theme.shadows.small, padding: 18 }}>
  <div style={{ fontWeight: 600, color: '#0f3460', marginBottom: 8 }}>Comparativa de resultados</div>
  <ResponsiveContainer width="100%" height={260}>
    <BarChart layout="vertical" data={[
              { name: 'Victorias', value: calcularEstadisticas()?.victorias || 0, color: '#0f3460' },
        { name: 'Podios', value: calcularEstadisticas()?.podios || 0, color: '#e4312e' },
        { name: 'Abandonos', value: calcularEstadisticas()?.abandonos || 0, color: '#FFFFFF' },
      { name: 'Sanciones', value: calcularEstadisticas()?.sancionesRecibidas || 0, color: '#b0b0c0' }
    ]} margin={{ top: 16, right: 16, left: 80, bottom: 16 }}>
      <XAxis type="number" tick={{ fill: '#0f3460', fontWeight: 500 }} axisLine={{ stroke: '#e4312e' }} tickLine={false} />
      <YAxis dataKey="name" type="category" tick={{ fill: '#0f3460', fontWeight: 600 }} axisLine={{ stroke: '#e4312e' }} tickLine={false} />
      <Tooltip contentStyle={{ background: '#FFFFFF', border: '1px solid #e4312e', color: '#0f3460' }} />
              <Bar dataKey="value" radius={[0, 8, 8, 0]}
          label={{ position: 'right', fill: '#0f3460', fontWeight: 600 }}>
        <Cell fill="#0f3460" />
        <Cell fill="#e4312e" />
        <Cell fill="#FFFFFF" stroke="#0f3460" strokeWidth={2} />
        <Cell fill="#b0b0c0" />
      </Bar>
    </BarChart>
  </ResponsiveContainer>
</div>
{/* ----------- FIN GRÁFICOS ----------- */}
            </>
          )}
          
          {activeTab === 'historial' && (
            <>
              <h3>Historial de Carreras</h3>
              <Table>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Carrera</th>
                    <th>Circuito</th>
                    <th>Posición</th>
                    <th>Puntos</th>
                    <th>Incidentes</th>
                  </tr>
                </thead>
                <tbody>
                  {getCarrerasPiloto().map((carrera: Carrera) => {
                    const resultado = carrera.resultados.find((r: any) => r.socioId === socio.id);
                    return (
                      <tr key={carrera.id}>
                        <td>{carrera.fecha}</td>
                        <td>{carrera.nombre}</td>
                        <td>{carrera.circuito}</td>
                        <td>{resultado?.posicion || '-'}</td>
                        <td>{resultado?.puntosTotales || 0}</td>
                        <td>{resultado?.sancionesAplicadas?.length || 0}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </>
          )}
          
          {activeTab === 'sanciones' && (
            <>
              <h3>Sanciones Recibidas</h3>
              <Table>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Carrera</th>
                    <th>Código</th>
                    <th>Descripción</th>
                    <th>Puntos</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {getSancionesPiloto().map((sancion: Sancion) => {
                    const carrera = data.carreras.find((c: Carrera) => c.id === sancion.carreraId);
                    return (
                      <tr key={sancion.id}>
                        <td>{sancion.fechaAplicacion}</td>
                        <td>{carrera?.nombre || '-'}</td>
                        <td>{sancion.codigoSancion || '-'}</td>
                        <td>{sancion.descripcion}</td>
                        <td>{sancion.puntosPenalizacion}</td>
                        <td>
                          <Button onClick={() => navigate(`/apelaciones/new?sancion=${sancion.id}`)}>
                            Apelar
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </>
          )}
          
          {activeTab === 'apelaciones' && (
            <>
              <h3>Apelaciones</h3>
              <p>Esta sección mostrará las apelaciones presentadas por el piloto.</p>
              {/* Aquí se implementaría la tabla de apelaciones cuando esté disponible */}
            </>
          )}
          
          {activeTab === 'licencia' && (
            <>
              <h3>Historial de Puntos de Licencia</h3>
              
              {/* Resumen de puntos de licencia */}
              <div style={{ 
                background: '#FFFFFF',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: theme.shadows.small,
                marginBottom: '30px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <div style={{ 
                  fontSize: '18px', 
                  fontWeight: 'bold',
                  marginBottom: '15px',
                  color: theme.colors.primary.main
                }}>
                  Estado Actual de Licencia
                </div>
                
                <div style={{ 
                  width: '200px',
                  height: '200px',
                  position: 'relative',
                  marginBottom: '20px'
                }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Puntos', value: socio.puntosLicencia || 12 },
                          { name: 'Perdidos', value: 12 - (socio.puntosLicencia || 12) }
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        startAngle={90}
                        endAngle={-270}
                        dataKey="value"
                      >
                        <Cell fill={(() => {
                          const puntos = socio.puntosLicencia || 12;
                          if (puntos >= 9) return theme.colors.success.main;
                          if (puntos >= 6) return theme.colors.warning.main;
                          return theme.colors.error.main;
                        })()} />
                        <Cell fill="#f0f0f0" />
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{ 
                    position: 'absolute', 
                    top: '50%', 
                    left: '50%', 
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '36px', fontWeight: 'bold', color: theme.colors.primary.main }}>
                      {socio.puntosLicencia || 12}
                    </div>
                    <div style={{ fontSize: '14px', color: theme.colors.text.secondary }}>de 12</div>
                  </div>
                </div>
                
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: 'bold',
                  marginBottom: '5px',
                  color: (() => {
                    const puntos = socio.puntosLicencia || 12;
                    if (puntos >= 9) return '#4caf50'; // Verde
                    if (puntos >= 6) return '#ff9800'; // Naranja
                    return '#f44336'; // Rojo
                  })()
                }}>
                  {(() => {
                    const puntos = socio.puntosLicencia || 12;
                    if (puntos >= 9) return 'Licencia en Buen Estado';
                    if (puntos >= 6) return 'Licencia con Advertencia';
                    if (puntos >= 3) return 'Licencia en Riesgo';
                    return 'Licencia en Peligro de Suspensión';
                  })()}
                </div>
              </div>
              
              {/* Historial de cambios en puntos de licencia */}
              <div style={{ 
                background: '#FFFFFF',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: theme.shadows.small
              }}>
                <h4 style={{ marginBottom: '20px', color: theme.colors.primary.main }}>Historial de Cambios</h4>
                
                {(() => {
                  const sanciones = getSancionesPiloto();
                  
                  // Si no hay sanciones, mostrar mensaje
                  if (sanciones.length === 0) {
                    return (
                      <div style={{ 
                        padding: '30px 20px', 
                        textAlign: 'center', 
                        color: theme.colors.text.secondary,
                        background: '#f8f9fa',
                        borderRadius: '8px'
                      }}>
                        <div style={{ fontSize: '18px', marginBottom: '10px' }}>
                          No hay registros de cambios en los puntos de licencia
                        </div>
                        <div>
                          Este piloto mantiene todos sus puntos de licencia intactos
                        </div>
                      </div>
                    );
                  }
                  
                  // Generar historial basado en sanciones
                  // Asumimos que cada sanción resta puntos de licencia
                  const puntosIniciales = 12;
                  let puntosActuales = socio.puntosLicencia || 12;
                  
                  // Ordenar sanciones por fecha (más recientes primero)
                  const sancionesOrdenadas = [...sanciones].sort((a, b) => {
                    const fechaA = a.fechaAplicacion ? new Date(a.fechaAplicacion) : new Date(0);
                    const fechaB = b.fechaAplicacion ? new Date(b.fechaAplicacion) : new Date(0);
                    return fechaB.getTime() - fechaA.getTime();
                  });
                  
                  // Calcular puntos perdidos por cada sanción
                  // Asumimos que la diferencia entre puntos iniciales y actuales
                  // se distribuye entre las sanciones existentes
                  const puntosPerdidos = puntosIniciales - puntosActuales;
                  const puntosPorSancion = sancionesOrdenadas.length > 0 
                    ? Math.ceil(puntosPerdidos / sancionesOrdenadas.length)
                    : 0;
                  
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      {/* Entrada para puntos iniciales */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        padding: '15px',
                        borderRadius: '8px',
                                        background: theme.colors.success.light || '#f1f8e9',
                borderLeft: `4px solid ${theme.colors.success.main}`
                      }}>
                        <div style={{ flex: '0 0 100px', fontWeight: 'bold' }}>
                          Inicial
                        </div>
                        <div style={{ flex: '1 1 auto' }}>
                          Puntos de licencia iniciales
                        </div>
                        <div style={{ 
                          flex: '0 0 80px', 
                          fontWeight: 'bold', 
                          textAlign: 'right',
                          color: theme.colors.success.main
                        }}>
                          +12 puntos
                        </div>
                      </div>
                      
                      {/* Entradas para cada sanción */}
                      {sancionesOrdenadas.map((sancion, index) => {
                        // Calcular puntos para esta sanción
                        // Si es la última sanción y quedan puntos por distribuir, asignar todos los restantes
                        const puntosSancion = index === sancionesOrdenadas.length - 1 
                          ? Math.min(puntosPerdidos - (puntosPorSancion * index), puntosPorSancion)
                          : puntosPorSancion;
                        
                        return (
                          <div key={sancion.id} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            padding: '15px',
                            borderRadius: '8px',
                                            background: theme.colors.error.light || '#ffebee',
                borderLeft: `4px solid ${theme.colors.error.main}`
                          }}>
                            <div style={{ flex: '0 0 100px', fontWeight: 'bold' }}>
                              {sancion.fechaAplicacion ? new Date(sancion.fechaAplicacion).toLocaleDateString() : 'Sin fecha'}
                            </div>
                            <div style={{ flex: '1 1 auto' }}>
                              {sancion.descripcion || 'Sanción por incidente en carrera'}
                              {sancion.carreraId && (
                                <div style={{ fontSize: '12px', color: theme.colors.text.secondary, marginTop: '5px' }}>
                                  Carrera: {(() => {
                                    const carrera = data.carreras.find(c => c.id === sancion.carreraId);
                                    return carrera ? carrera.nombre : 'Desconocida';
                                  })()}
                                </div>
                              )}
                            </div>
                            <div style={{ 
                              flex: '0 0 80px', 
                              fontWeight: 'bold', 
                              textAlign: 'right',
                              color: theme.colors.error.main
                            }}>
                              -{puntosSancion} {puntosSancion === 1 ? 'punto' : 'puntos'}
                            </div>
                          </div>
                        );
                      })}
                      
                      {/* Entrada para estado actual */}
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        padding: '15px',
                        borderRadius: '8px',
                                        background: theme.colors.info.light || '#e3f2fd',
                borderLeft: `4px solid ${theme.colors.info.main}`,
                        marginTop: '10px'
                      }}>
                        <div style={{ flex: '0 0 100px', fontWeight: 'bold' }}>
                          Actual
                        </div>
                        <div style={{ flex: '1 1 auto' }}>
                          Puntos de licencia restantes
                        </div>
                        <div style={{ 
                          flex: '0 0 80px', 
                          fontWeight: 'bold', 
                          textAlign: 'right',
                          color: theme.colors.info.main
                        }}>
                          {puntosActuales} {puntosActuales === 1 ? 'punto' : 'puntos'}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
              
              {/* Información sobre el sistema de puntos */}
              <div style={{ 
                background: '#FFFFFF',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: theme.shadows.small,
                marginTop: '30px'
              }}>
                <h4 style={{ marginBottom: '15px', color: theme.colors.primary.main }}>Sistema de Puntos de Licencia</h4>
                <p style={{ marginBottom: '15px' }}>
                  Cada piloto comienza con 12 puntos de licencia. Las sanciones por infracciones en carrera pueden resultar en pérdida de puntos:
                </p>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: theme.colors.error.main }}></div>
                    <div><strong>Infracciones leves:</strong> -1 punto</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: theme.colors.error.main }}></div>
                    <div><strong>Infracciones moderadas:</strong> -2 puntos</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: theme.colors.error.main }}></div>
                    <div><strong>Infracciones graves:</strong> -3 o más puntos</div>
                  </div>
                </div>
                
                <p style={{ marginBottom: '15px' }}>
                  <strong>Consecuencias:</strong>
                </p>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: theme.colors.warning.main }}></div>
                    <div><strong>6-8 puntos:</strong> Advertencia</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: theme.colors.error.main }}></div>
                    <div><strong>3-5 puntos:</strong> Riesgo de suspensión</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: theme.colors.error.dark }}></div>
                    <div><strong>0-2 puntos:</strong> Suspensión inminente</div>
                  </div>
                </div>
              </div>
            </>
          )}
          
          {activeTab === 'reputacion' && (
            <>
              <h3>Sistema de Reputación</h3>
              
              {/* Puntuación de reputación con medidor visual */}
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                marginBottom: '30px',
                background: '#FFFFFF',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: theme.shadows.small
              }}>
                <h4 style={{ marginBottom: '15px', color: theme.colors.primary.main }}>Puntuación de Reputación</h4>
                
                {/* Medidor circular de reputación */}
                <div style={{ 
                  position: 'relative', 
                  width: '200px', 
                  height: '200px',
                  marginBottom: '20px'
                }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Reputación', value: calcularEstadisticas()?.reputacion || 0 },
                          { name: 'Restante', value: 100 - (calcularEstadisticas()?.reputacion || 0) }
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        startAngle={90}
                        endAngle={-270}
                        dataKey="value"
                      >
                        <Cell fill={theme.colors.primary.main} />
                        <Cell fill="#f0f0f0" />
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{ 
                    position: 'absolute', 
                    top: '50%', 
                    left: '50%', 
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '36px', fontWeight: 'bold', color: theme.colors.primary.main }}>
                      {calcularEstadisticas()?.reputacion || 0}
                    </div>
                    <div style={{ fontSize: '14px', color: theme.colors.text.secondary }}>de 100</div>
                  </div>
                </div>
                
                {/* Nivel de reputación */}
                <div style={{ 
                  fontSize: '24px', 
                  fontWeight: 'bold',
                  color: (() => {
                    const stats = calcularEstadisticas();
                    const reputacion = stats ? stats.reputacion : 50;
                    if (reputacion >= 80) return theme.colors.success.main;
                    if (reputacion >= 60) return theme.colors.warning.main;
                    return theme.colors.error.main;
                  })()
                }}>
                  {(() => {
                    const stats = calcularEstadisticas();
                    const reputacion = stats ? stats.reputacion : 50;
                    if (reputacion >= 80) return 'Excelente';
                    if (reputacion >= 60) return 'Buena';
                    if (reputacion >= 40) return 'Regular';
                    return 'Baja';
                  })()}
                </div>
                {/* Mostrar posición actual en la liga */}
                <div style={{ 
                  marginTop: '15px',
                  fontSize: '16px',
                  color: theme.colors.text.secondary
                }}>
                  Posición actual: {(() => {
                    if (!socio) return 'No disponible';
                    
                    const stats = calcularEstadisticas();
                    if (!stats) return 'No disponible';
                    
                    return `${stats.posicionGeneral}° de ${stats.totalPilotosEnLiga}`;
                  })()}
                </div>
              </div>
              
              {/* Componentes de la reputación */}
              <div style={{ 
                background: '#FFFFFF',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: theme.shadows.small,
                marginBottom: '30px'
              }}>
                <h4 style={{ marginBottom: '15px', color: theme.colors.primary.main }}>Componentes de la Reputación</h4>
                <p style={{ marginBottom: '20px' }}>La reputación se calcula en base a tres factores principales:</p>
                
                {/* Gráfico de barras para los componentes */}
                <div style={{ height: '300px', marginBottom: '20px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={(() => {
                        // Calcular los componentes de la reputación
                        if (!socio) return [];
                        
                        // Obtener las estadísticas completas para evitar múltiples llamadas
                        const estadisticas = calcularEstadisticas();
                        
                        // DEPURACIÓN: Mostrar estadísticas completas
                        console.log('REPUTACIÓN GRÁFICO - Estadísticas completas:', estadisticas);
                        
                        const stats = {
                          carrerasCompletadas: estadisticas?.carrerasCompletadas || 0,
                          victorias: estadisticas?.victorias || 0,
                          podios: estadisticas?.podios || 0,
                          abandonos: estadisticas?.abandonos || 0,
                          sancionesRecibidas: estadisticas?.sancionesRecibidas || 0,
                          posicionMediaCarrera: estadisticas?.posicionMediaCarrera || 0,
                          totalPuntos: estadisticas?.totalPuntos || socio.puntosTotales || 0,
                          posicionGeneral: estadisticas?.posicionGeneral || 0,
                          totalPilotosEnLiga: estadisticas?.totalPilotosEnLiga || 0
                        };
                        
                        // DEPURACIÓN: Mostrar stats para el cálculo
                        console.log('REPUTACIÓN GRÁFICO - Stats para cálculo:', stats);
                        
                        // Si no hay carreras completadas
                        if (stats.carrerasCompletadas === 0) {
                          return [
                            { nombre: 'Posición en Clasificación', valor: 20, maximo: 40 },
                            { nombre: 'Resultados en Carreras', valor: 20, maximo: 40 },
                            { nombre: 'Sanciones', valor: 10, maximo: 20 }
                          ];
                        }
                        
                        // 1. Factor de posición en la clasificación general
                        // Usar directamente los valores de posición de las estadísticas calculadas
                        const posicionGeneral = stats.posicionGeneral;
                        const totalPilotosEnLiga = stats.totalPilotosEnLiga;
                        
                        // DEPURACIÓN: Mostrar datos de posición en la liga
                        console.log(`REPUTACIÓN GRÁFICO - Posición del piloto: ${posicionGeneral} de ${totalPilotosEnLiga}`);
                        
                        // Calcular el factor de posición (mejor posición = mayor puntuación)
                        // Si es el primero, obtiene 40 puntos completos
                        // Si está en la mitad, obtiene 20 puntos
                        // Si es el último, obtiene 0 puntos
                        let factorPosicion = 40;
                        
                        if (totalPilotosEnLiga > 1 && posicionGeneral > 1) {
                          // Calcular qué porcentaje representa su posición en la clasificación
                          // Ejemplo: si es 9º de 32, está en el 25% superior (buena posición)
                          const porcentajePosicion = (posicionGeneral - 1) / (totalPilotosEnLiga - 1);
                          factorPosicion = Math.round(40 * (1 - porcentajePosicion));
                          
                          // DEPURACIÓN: Mostrar cálculo detallado
                          console.log(`REPUTACIÓN GRÁFICO - Cálculo: (${posicionGeneral}-1)/(${totalPilotosEnLiga}-1) = ${porcentajePosicion.toFixed(3)}`);
                          console.log(`REPUTACIÓN GRÁFICO - Factor posición: 40 * (1 - ${porcentajePosicion.toFixed(3)}) = ${factorPosicion}`);
                        }
                        
                        // 2. Factor de resultados en carreras
                        // Mejorar el cálculo para dar más peso a los podios y victorias
                        const ratioVictorias = stats.victorias / stats.carrerasCompletadas;
                        const ratioPodios = stats.podios / stats.carrerasCompletadas;
                        const ratioAbandonos = stats.abandonos / stats.carrerasCompletadas;
                        
                        // Base de 20 puntos + bonificaciones por victorias y podios - penalizaciones por abandonos
                        // Si tiene 100% de victorias, suma 20 puntos adicionales
                        // Si tiene 100% de podios (sin ser victorias), suma 15 puntos adicionales
                        // Si tiene 100% de abandonos, resta 15 puntos
                        let factorResultados = 20;
                        
                        // Bonificación por victorias (hasta 20 puntos adicionales)
                        factorResultados += Math.round(ratioVictorias * 20);
                        
                        // Bonificación por podios que no son victorias (hasta 15 puntos adicionales)
                        const ratioPodiosNoVictorias = Math.max(0, (stats.podios - stats.victorias) / stats.carrerasCompletadas);
                        factorResultados += Math.round(ratioPodiosNoVictorias * 15);
                        
                        // Penalización por abandonos (hasta -15 puntos)
                        factorResultados -= Math.round(ratioAbandonos * 15);
                        
                        // Asegurar que esté en el rango 0-40
                        factorResultados = Math.max(0, Math.min(40, factorResultados));
                        
                        // 3. Factor de sanciones
                        // Si no tiene sanciones, obtiene los 20 puntos completos
                        // Cada sanción por carrera resta puntos
                        let factorSanciones = 20;
                        if (stats.carrerasCompletadas > 0 && stats.sancionesRecibidas > 0) {
                          const ratioSanciones = stats.sancionesRecibidas / stats.carrerasCompletadas;
                          // Una sanción por carrera (ratio=1) resta 15 puntos
                          // Más de 1.5 sanciones por carrera (ratio>1.5) lleva a 0 puntos
                          factorSanciones = Math.max(0, 20 - Math.round(ratioSanciones * 15));
                        }
                        
                        return [
                          { nombre: 'Posición en Clasificación', valor: Math.round(factorPosicion), maximo: 40 },
                          { nombre: 'Resultados en Carreras', valor: Math.round(factorResultados), maximo: 40 },
                          { nombre: 'Sanciones', valor: Math.round(factorSanciones), maximo: 20 }
                        ];
                      })()}
                      layout="vertical"
                      margin={{ top: 20, right: 30, left: 200, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" domain={[0, 40]} />
                      <YAxis dataKey="nombre" type="category" width={140} />
                      <Tooltip formatter={(value, name, props) => [`${value} de ${props.payload.maximo} puntos`, name]} />
                      <Legend />
                      <Bar dataKey="valor" fill={theme.colors.primary.main} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Explicación de los componentes */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  <div>
                    <h5 style={{ color: theme.colors.primary.main, marginBottom: '5px' }}>Posición en Clasificación (40%)</h5>
                    <p>Se basa en la posición del piloto en la clasificación general de su liga. Mejor posición = mayor puntuación.</p>
                  </div>
                  <div>
                    <h5 style={{ color: theme.colors.primary.main, marginBottom: '5px' }}>Resultados en Carreras (40%)</h5>
                    <p>Considera victorias, podios y abandonos. Más victorias y podios suman puntos, mientras que los abandonos restan.</p>
                  </div>
                  <div>
                    <h5 style={{ color: theme.colors.primary.main, marginBottom: '5px' }}>Sanciones (20%)</h5>
                    <p>Evalúa las sanciones recibidas en relación al número de carreras. Menos sanciones = mayor puntuación.</p>
                  </div>
                </div>
              </div>
              
              {/* Evolución de la reputación */}
              <div style={{ 
                background: '#FFFFFF',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: theme.shadows.small
              }}>
                <h4 style={{ marginBottom: '15px', color: theme.colors.primary.main }}>Estadísticas Clave</h4>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '20px', marginBottom: '20px' }}>
                  <div style={{ textAlign: 'center', padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ fontSize: '14px', color: theme.colors.text.secondary }}>Posición en Liga</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: theme.colors.primary.main }}>
                      {(() => {
                        if (!socio) return '-';
                        
                        // Usar la función obtenerPosicionEnLiga para obtener la posición del piloto
                        const stats = calcularEstadisticas();
                        if (!stats) return '-';
                        
                        return `${stats.posicionGeneral}° de ${stats.totalPilotosEnLiga}`;
                      })()}
                    </div>
                  </div>
                  
                  <div style={{ textAlign: 'center', padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ fontSize: '14px', color: theme.colors.text.secondary }}>Ratio Victoria</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: theme.colors.primary.main }}>
                      {(() => {
                        const stats = calcularEstadisticas();
                        if (!stats || stats.carrerasCompletadas === 0) return '0%';
                        return `${Math.round((stats.victorias / stats.carrerasCompletadas) * 100)}%`;
                      })()}
                    </div>
                  </div>
                  
                  <div style={{ textAlign: 'center', padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ fontSize: '14px', color: theme.colors.text.secondary }}>Ratio Podio</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: theme.colors.primary.main }}>
                      {(() => {
                        const stats = calcularEstadisticas();
                        if (!stats || stats.carrerasCompletadas === 0) return '0%';
                        return `${Math.round((stats.podios / stats.carrerasCompletadas) * 100)}%`;
                      })()}
                    </div>
                  </div>
                  
                  <div style={{ textAlign: 'center', padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ fontSize: '14px', color: theme.colors.text.secondary }}>Sanciones por Carrera</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: theme.colors.primary.main }}>
                      {(() => {
                        const stats = calcularEstadisticas();
                        if (!stats || stats.carrerasCompletadas === 0) return '0';
                        return (stats.sancionesRecibidas / stats.carrerasCompletadas).toFixed(2);
                      })()}
                    </div>
                  </div>
                </div>
                
                <div style={{ marginTop: '30px' }}>
                  <h5 style={{ marginBottom: '10px', color: theme.colors.primary.main }}>Consejos para mejorar tu reputación</h5>
                  <ul style={{ paddingLeft: '20px' }}>
                    <li>Termina todas tus carreras sin abandonar</li>
                    <li>Evita maniobras que puedan resultar en sanciones</li>
                    <li>Mejora tu posición en la clasificación general</li>
                    <li>Consigue más podios y victorias</li>
                    <li>Mantén una conducción limpia y deportiva</li>
                  </ul>
                </div>
              </div>
            </>
          )}
        </TabContent>
      </TabsContainer>
    </ProfileContainer>
  );
};

export default SocioProfile;
